import { OktaContext } from '@okta/okta-react';
import {
  useLocationHash,
  useLocationPathname,
  useLocationSearch,
  useLocationState,
  useNavigate,
  Outlet,
  Routes,
  Route,
} from '@rocketlane/rocket-router';
import { lazy, memo, Suspense, useContext, useEffect } from 'react';
import { AccountStatus } from 'api/account';
import { UserType } from 'api/models';
import { MentionService } from 'components/atoms/mention-service';
import { TaskPaneHashRewrite } from 'components/atoms/task-pane-hash-rewrite';
import { WaitForPageSettle } from 'components/atoms/wait-for-page-settle/wait-for-page-settle';
import { MagicLinkMessageType } from 'components/molecules/session-setup/magic-link-generate';
import { AgentsSidePanel } from 'components/organisms/agents-side-panel';
import { NotificationsDrawer } from 'components/organisms/notifications/drawer/notifications-drawer';
import { PreviewWrapper } from 'components/organisms/projects/project-preview/preview';
import { LazyRouterShell } from 'components/organisms/router-shell/lazy-imports';
import { InitialAppFetch } from 'components/pages/initial-app-fetch';
import { BEAMER_URL, RoutePaths } from 'constant';
import { useCustomerPreviewForCustomerPortalV2 } from 'contexts/providers';
import { AgentsSidePanelProvider } from 'contexts/providers/agents-side-panel';
import { GlobalSearchContextProvider } from 'contexts/providers/global-search';
import { NotificationsDrawerContextProvider } from 'contexts/providers/notifications-drawer';
import { ProjectSpacesRearrangeModalContextProvider } from 'contexts/providers/rearrange-modal';
import { TaskPaneContextProvider } from 'contexts/providers/task-pane';
import { useAccount } from 'hooks/use-account';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useRouteLoadEventInitialiser } from 'hooks/use-data-dog-logger';
import { useHeadlessMode } from 'hooks/use-headless-mode';
import {
  clearColumnOrderTableFromIndexedDb,
  useInitIndexDbListeners,
} from 'hooks/use-idb';
import { useIsAppInCypress } from 'hooks/use-is-app-in-cypress';
import { useQueryParams } from 'hooks/use-query-params';
import { useUser } from 'hooks/use-user';
import { LdsUtils } from 'marketplace/utils/lds-utils';
import { useAccountMetaQuery } from 'queries';
import { useAccountDataQuery } from 'queries/account';
import { ActiveTab } from 'services';
import { BootTransitionSurface } from 'utils/boot-transition-surface';
import { getDevEnv } from 'utils/is-localhost';
import { isNitroEnvironment } from 'utils/is-nitro-env';
import { redirectToOAuth } from 'utils/oauth-redirect';
import { unreachable } from 'utils/unreachable';
import { AccountAssessment } from './account-assessment/account-assessment';
import { LazyBootApp } from './app-boot-data';
import { CustomSecureRoute } from './custom-secure-route';
import { PortalBotRoutes } from './customer/portal-bot-routes';
import { AddLdsScripts } from './marketplace/add-lds-scripts';
import { AddMarketplaceClientEventScriptsForAllApps } from './marketplace/add-marketplace-client-event-scripts';
import { ROUTE_PATHS } from './routes-paths';
import { LazyIntroModal } from './vendor/lazy-imports';

// Lazy load TaskDrawer and GlobalSearch components
const TaskDrawer = lazy(() =>
  import('components/organisms/task-drawer/task-drawer').then((m) => ({
    default: m.TaskDrawer,
  })),
);

const PolicyConflictResolution = lazy(() =>
  import(
    'components/organisms/projects/ask-ai/policy-conflict-resolution'
  ).then((m) => ({ default: m.PolicyConflictResolution })),
);

const GlobalSearch = lazy(() =>
  import('components/organisms/global-search-modal').then((m) => ({
    default: m.GlobalSearch,
  })),
);

// CP space-tab is only rendered when the URL matches a CP space-tab path and
// the user is a CustomerPortalBot. Lazy-load to keep the ~90 KB chunk
// (space-tab.tsx + 21 modules) out of the app shell. Same component is also
// lazy-loaded in render-portal.tsx and portal-bot-routes.tsx.
const CpSpaceTabPage = lazy(() =>
  import(
    'components/organisms/customer-portal/widgets/space/cp-space-tab-page'
  ).then((m) => ({ default: m.CpSpaceTabPage })),
);

const CustomerRoutes = lazy(() => import('./customer/customer'));

const CLEAR_IDB_LOCALSTORAGE_KEY = '__clear_idb_502';

const Cerebrum = () => {
  const { accountId } = useAccount();
  const { isLoading, isPreviewAvailable } =
    useCustomerPreviewForCustomerPortalV2();
  const pathname = useLocationPathname();
  const isInCypress = useIsAppInCypress();
  const isSnapshot = useHeadlessMode();
  const { user } = useUser();
  const { data: accountData } = useAccountDataQuery();
  const { isLocal } = getDevEnv();
  const isNitro = isNitroEnvironment();

  useEffect(() => {
    async function clearIndexedDBForPosist() {
      const apiKey = localStorage.getItem('__api_key');
      const flag = localStorage.getItem(CLEAR_IDB_LOCALSTORAGE_KEY);

      if (!flag && accountId === 502) {
        await clearColumnOrderTableFromIndexedDb();

        localStorage.clear();
        localStorage.setItem(
          CLEAR_IDB_LOCALSTORAGE_KEY,
          JSON.stringify(new Date().getTime()),
        );
        if (apiKey) {
          localStorage.setItem('__api_key', apiKey);
        }
        globalThis.location.reload();
      }
    }
    clearIndexedDBForPosist();
  }, [accountId]);

  useEffect(() => {
    if ([UserType.Native, UserType.Partner].includes(user?.userType)) {
      const isBeamerPresent = Boolean(
        document.querySelector(`script[src="${BEAMER_URL}"]`),
      );

      if (
        !isInCypress &&
        !isSnapshot &&
        !isBeamerPresent &&
        !isLocal &&
        !isNitro &&
        accountData
      ) {
        const beamerScriptEl = document.createElement('script');

        beamerScriptEl.setAttribute('type', 'text/javascript');

        beamerScriptEl.setAttribute('src', BEAMER_URL);

        document.body.appendChild(beamerScriptEl);

        beamerScriptEl.onload = () => {
          (globalThis as any).Beamer?.update({
            user_firstname: user.firstName,
            user_lastname: user.lastName,
            user_email: user.emailId,
            user_id: user.userId,
            is_paid: [
              AccountStatus.Active,
              AccountStatus.Grace,
              AccountStatus.Paid,
            ].includes(accountData.account.status),
          });
        };
      }
    }
  }, [isInCypress, isSnapshot, isLocal, isNitro, user, accountData]);

  if (isLoading) {
    return null;
  }

  if (!user?.userType) {
    return null;
  }

  if (isPreviewAvailable || pathname === 'notifications') {
    return <CustomerRoutes />;
  }

  switch (user.userType) {
    case UserType.CustomerPartner:
    case UserType.External:
      return (
        <>
          <CustomerRoutes />
        </>
      );
    case UserType.CustomerPortalBot: {
      return (
        <>
          <PortalBotRoutes />
          <Routes>
            <Route
              path={RoutePaths.customerPortalSpaceTabById}
              // path="/projects/1922/spaces/1389/tabs/1157"
              caseSensitive
              element={
                user?.userType === UserType.CustomerPortalBot ? (
                  <CustomSecureRoute>
                    <CpSpaceTabPage />
                  </CustomSecureRoute>
                ) : null
              }
            />
            <Route
              path={RoutePaths.customerPortalSheetById}
              caseSensitive
              element={<></>}
            />
          </Routes>
        </>
      );
    }
    case UserType.Native:
    case UserType.Partner:
      return (
        <AccountAssessment>
          <LazyRouterShell>
            <Outlet />
          </LazyRouterShell>
          <LazyIntroModal />
        </AccountAssessment>
      );
    default:
      return unreachable(user?.userType);
  }
};

const MemoisedCerbrum = memo(() => {
  return (
    <>
      <Cerebrum />
      <MentionService />
    </>
  );
});

MemoisedCerbrum.displayName = 'MemoisedCerbrum';

enum UserTypeQueryParamValue {
  Customer = '2',
}
const USER_TYPE_QUERY_PARAM_KEY = 'ut';
const TOKEN_QUERY_PARAM_KEY = 'token';

/**
 * Okta `authState` when `<Security>` is mounted; otherwise session from authService
 * (cookies-only accounts — see `SetupOkta`).
 */
function useAuthStateWithCookiesFallback() {
  const oktaContext = useContext(OktaContext);
  const { isAuthenticated: customIsAuthenticated } = useCustomAuth();

  return oktaContext
    ? oktaContext.authState
    : { isAuthenticated: customIsAuthenticated };
}

/**
 * Strips `ut` / `token` query params from the URL once the user is authenticated.
 * Isolated so that re-renders caused by location changes don't affect ProtectedRoutes.
 */
const QueryParamCleaner = () => {
  const authState = useAuthStateWithCookiesFallback();

  const nav = useNavigate();
  const params = useQueryParams();
  const hash = useLocationHash();
  const pathname = useLocationPathname();
  const search = useLocationSearch();

  useEffect(() => {
    if (authState && authState.isAuthenticated) {
      const isCustomerViewTypeParamPresent =
        params.get(USER_TYPE_QUERY_PARAM_KEY) ===
        UserTypeQueryParamValue.Customer;
      const isTokenParamPresent = params.has(TOKEN_QUERY_PARAM_KEY);

      if (isCustomerViewTypeParamPresent || isTokenParamPresent) {
        if (isCustomerViewTypeParamPresent) {
          params.delete(USER_TYPE_QUERY_PARAM_KEY);
        }
        if (isTokenParamPresent) {
          params.delete(TOKEN_QUERY_PARAM_KEY);
        }
        const currentPathname = globalThis.location.pathname;

        nav(
          { pathname: currentPathname, hash, search: params.toString() },
          { replace: true },
        );
      }
    }
  }, [authState, params, pathname, hash, search, nav]);

  return null;
};

/**
 * Redirects unauthenticated users to login or magic-link page.
 * Isolated so that re-renders caused by location changes don't affect ProtectedRoutes.
 */
const UnauthenticatedRedirector = () => {
  const authState = useAuthStateWithCookiesFallback();
  const nav = useNavigate();
  const params = useQueryParams();
  const hash = useLocationHash();
  const state = useLocationState();
  const { data: accountMeta } = useAccountMetaQuery();

  useEffect(() => {
    // authState === null/undefined means Okta is still initialising — do NOT
    // redirect yet. Redirecting while pending causes React Router to switch to
    // NonProtectedRoutesLayout, unmounting ProtectedRoutes/AppShell before the
    // session check completes. Only redirect once Okta has definitively checked
    // and found the user is not authenticated.
    if (!authState) {
      return;
    }

    if (!authState.isAuthenticated) {
      /* NOTE: if the url consist of ut=2 which is customer type and the user is not authenticated, then we need to take them to magic link page */
      if (
        params.get(USER_TYPE_QUERY_PARAM_KEY) ===
        UserTypeQueryParamValue.Customer
      ) {
        if (params.has(TOKEN_QUERY_PARAM_KEY)) {
          ActiveTab.save();
          nav(
            {
              pathname: ROUTE_PATHS.MAGIC_LINK,
              search: params.toString(),
              hash,
            },
            {
              state: {
                ...(state as Record<string, unknown>),
                messageType: MagicLinkMessageType.NotSignedIn,
              },
            },
          );
        } else if (accountMeta?.okta?.cookiesEnabled) {
          ActiveTab.save();
          redirectToOAuth(accountMeta?.okta?.clientId);
        } else {
          ActiveTab.save();
          nav(
            {
              pathname: `${ROUTE_PATHS.MAGIC_LINK}/${ROUTE_PATHS.NEW}`,
              search: params.toString(),
              hash,
            },
            {
              state: {
                ...(state as Record<string, unknown>),
                messageType: MagicLinkMessageType.NotSignedIn,
              },
            },
          );
        }
      } else {
        ActiveTab.save();
        nav({ pathname: ROUTE_PATHS.LOGIN, search: params.toString(), hash });
      }
    }
  }, [
    nav,
    params,
    hash,
    state,
    accountMeta?.okta?.clientId,
    accountMeta?.okta?.cookiesEnabled,
    authState,
  ]);

  return null;
};

/**
 * Wraps all navigation-triggered side effects that don't belong in ProtectedRoutes.
 * Isolated so pathname/hash changes don't re-render the main app tree.
 */
const RouteSideEffects = () => {
  useRouteLoadEventInitialiser();

  return null;
};

const AppShell = memo(() => (
  <PreviewWrapper>
    <Suspense fallback={<BootTransitionSurface />}>
      <LazyBootApp>
        <TaskPaneContextProvider>
          <NotificationsDrawerContextProvider>
            <ProjectSpacesRearrangeModalContextProvider>
              <GlobalSearchContextProvider>
                <AgentsSidePanelProvider>
                  <GlobalSearch />
                  <MemoisedCerbrum />
                  <TaskDrawer />
                  <WaitForPageSettle timeout={6000}>
                    <NotificationsDrawer />
                  </WaitForPageSettle>
                  <AgentsSidePanel />
                  <TaskPaneHashRewrite />
                  <InitialAppFetch />
                  {LdsUtils.isDevModeOn() ? <AddLdsScripts /> : <></>}
                  <AddMarketplaceClientEventScriptsForAllApps />

                  <Suspense fallback={null}>
                    <PolicyConflictResolution />
                  </Suspense>
                </AgentsSidePanelProvider>
              </GlobalSearchContextProvider>
            </ProjectSpacesRearrangeModalContextProvider>
          </NotificationsDrawerContextProvider>
        </TaskPaneContextProvider>
      </LazyBootApp>
    </Suspense>
  </PreviewWrapper>
));

// Module-level latch: survives ProtectedRoutes unmount/remount (e.g. Security
// resetting during React 18 StrictMode). Once authState has been seen as
// truthy, we keep AppShell in the tree permanently. UnauthenticatedRedirector
// handles genuine logouts via navigation.
let _appShellUnlocked = false;

export const ProtectedRoutes = () => {
  const authState = useAuthStateWithCookiesFallback();

  if (authState?.isAuthenticated) {
    _appShellUnlocked = true;
  }

  // Keep AppShell mounted only while auth is truthy or still initialising (null).
  // If auth is definitively false, hide immediately so ProjectListV2Page (and any
  // other protected page) is unmounted *before* UnauthenticatedRedirector's nav()
  // fires — preventing a mid-transition crash from useUser() throwing.
  const showingAppShell =
    _appShellUnlocked && authState?.isAuthenticated !== false;

  useInitIndexDbListeners();

  return (
    <>
      <QueryParamCleaner />
      <UnauthenticatedRedirector />
      <RouteSideEffects />
      {showingAppShell ? <AppShell /> : null}
    </>
  );
};
